/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { cssMap, jsx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type AgentKnowledgeConfiguration,
	type AgentScenarioKnowledgeSource,
	type MappedAgentKnowledgeConfiguration,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

import {
	formatKnowledgeSourceLabel,
	iconMapper,
} from '../../common/ui/knowledge-sources/index.tsx';
import { SiteName } from '../../common/ui/site-name/SiteName.tsx';
import { getAtlasHost } from '../../common/utils/atlas-url/index.ts';
import { isSiteScopedKnowledgeSource } from '../../common/utils/is-site-scoped-knowledge-source.ts';
import { shouldShowMultiSiteKnowledgeExperience } from '../../common/utils/should-show-multi-site-knowledge-experience.ts';
import {
	type AgentKnowledgeFilter,
	getEnabledKnowledgeFiltersFromAgent,
	getEnabledKnowlegeArisFromAgent,
} from '../../utils/agents/index.tsx';
import type { AgentDetailsWithAggData } from '../agent-details/index.ts';

const styles = cssMap({
	wrapper: {
		minHeight: '20px',
	},

	iconWrapper: {
		height: '24px',
		width: '24px',
		display: 'flex',
		flexFlow: 'column nowrap',
	},

	siteSources: {
		paddingInlineStart: token('space.300'),
	},
});

export type KnowledgeSourceWithConfiguration = AgentScenarioKnowledgeSource &
	AgentKnowledgeConfiguration;

type AgentWithKnowledge = Pick<AgentDetailsWithAggData, 'scenarios' | 'knowledge_sources'>;

type KnowledgeSectionPropsForAgent =
	| {
			aris: string[];
			knowledgeFilters?: never;
			knowledgeSiteGroups?: never;
			shouldGroupKnowledgeBySite: false;
	  }
	| {
			aris?: never;
			knowledgeFilters: AgentKnowledgeFilter[];
			knowledgeSiteGroups: KnowledgeSiteGroup[];
			shouldGroupKnowledgeBySite: boolean;
	  };

type KnowledgeRenderForAgent = {
	knowledgeProductsRender: ReactNode[] | null;
	knowledgeSectionProps: KnowledgeSectionPropsForAgent;
};

type KnowledgeRenderForAgentParams = {
	agent: AgentWithKnowledge;
	cloudUrl?: string;
};

export type KnowledgeSiteGroup = {
	cloudId: string;
	filters: AgentKnowledgeFilter[];
	products: ReactNode[];
};

type KnowledgeProductsRenderParams = {
	agent: AgentWithKnowledge;
	allKnowledgeSourcesFiltered: Array<KnowledgeSourceWithConfiguration>;
	cloudUrl?: string;
	currentCloudId?: string;
	shouldShowMultiSiteExperience: boolean;
};

type KnowledgeProductsRenderingParams = KnowledgeProductsRenderParams & {
	knowledgeFilters?: AgentKnowledgeFilter[];
};

export const getIconRender = (
	knowledgeSource: KnowledgeSourceWithConfiguration | MappedAgentKnowledgeConfiguration,
): JSX.Element => {
	//We cannot load the icon via iconUrl for VR tests so use iconMapper
	if (process.env.NODE_ENV === 'development') {
		return iconMapper[knowledgeSource.source];
	}
	return (
		<Box xcss={styles.iconWrapper}>
			<img src={knowledgeSource.iconUrl} alt="" />
		</Box>
	);
};

const KnowledgeSourceProductUsingBEFields = ({
	knowledgeSource,
	cloudUrl,
}: {
	knowledgeSource: KnowledgeSourceWithConfiguration;
	cloudUrl?: string;
}) => {
	const LinkOrText = cloudUrl ? Link : Text;

	const getLabelRender = () => {
		switch (knowledgeSource.source) {
			case 'jira':
				return (
					<LinkOrText maxLines={1} href={`${cloudUrl}/jira`} target="_blank">
						{knowledgeSource.friendlyName}
					</LinkOrText>
				);
			case 'confluence':
				return (
					<LinkOrText maxLines={1} href={`${cloudUrl}/wiki`} target="_blank">
						{knowledgeSource.friendlyName}
					</LinkOrText>
				);
			case 'atlas':
				return (
					<Link href={getAtlasHost()} target="_blank">
						{knowledgeSource.friendlyName}
					</Link>
				);
			default:
				return (
					<Text maxLines={1}>
						{knowledgeSource.friendlyName || formatKnowledgeSourceLabel(knowledgeSource.source)}
					</Text>
				);
		}
	};

	return (
		<Inline xcss={styles.wrapper} alignBlock="center" space="space.075">
			{getIconRender(knowledgeSource)}
			{getLabelRender()}
		</Inline>
	);
};

export const isKnowledgeScopeEnabled = (
	agent: Pick<AgentDetailsWithAggData, 'scenarios' | 'knowledge_sources'>,
): boolean => {
	const enabledSources =
		agent.scenarios
			?.filter((scenario) => scenario.isActive && scenario.isValid?.value !== false)
			?.flatMap((scenario) => scenario.knowledgeSources)
			.filter((source) => source?.enabled) ?? [];

	return enabledSources.length > 0;
};

const isSameKnowledgeSource = (
	a: { cloudId?: string | null; source: string },
	b: { cloudId?: string | null; source: string },
): boolean => a.source === b.source && (a.cloudId ?? null) === (b.cloudId ?? null);

export const getAgentKnowledgeSources = (
	agent: Pick<AgentDetailsWithAggData, 'scenarios' | 'knowledge_sources'>,
): AgentScenarioKnowledgeSource[] => {
	return (
		agent.scenarios
			?.filter((scenario) => scenario.isActive && scenario.isValid?.value !== false)
			?.flatMap((scenario) => scenario.knowledgeSources?.sources)
			?.reduce((acc, source) => {
				if (!source) {
					return acc;
				}
				const alreadyAdded = fg('rovo_agents_unit_scoped')
					? acc.some((s) => isSameKnowledgeSource(s, source))
					: acc.some((s) => s.source === source.source);
				if (alreadyAdded) {
					return acc;
				}
				acc.push({
					enabled: source.enabled,
					source: source.source,
					...(source.cloudId && fg('rovo_agents_unit_scoped') ? { cloudId: source.cloudId } : {}),
					filters: {
						parent_filter: source.filters?.parentFilter ?? [],
						space_filter: source.filters?.spaceFilter ?? [],
						project_filter: source.filters?.projectFilter ?? [],
					},
				});
				return acc;
			}, [] as AgentScenarioKnowledgeSource[]) ?? []
	);
};

const getKnowledgeProductsRendering = ({
	agent,
	allKnowledgeSourcesFiltered,
	cloudUrl,
	currentCloudId,
	knowledgeFilters = [],
	shouldShowMultiSiteExperience,
}: KnowledgeProductsRenderingParams): {
	knowledgeSiteGroups: KnowledgeSiteGroup[];
	knowledgeProductsRender: ReactNode[] | null;
} => {
	let sources = getAgentKnowledgeSources(agent);
	const knowledgeScopeEnabled = isKnowledgeScopeEnabled(agent);
	let processedSources = sources;

	// When knowledge scope is not enabled, BE by default will use all the available knowledge sources, including any new ones that added
	// We don't want to render any specific knowledge sources, instead defaulting to `All organizational knowledge`
	if (!knowledgeScopeEnabled) {
		processedSources = [];
	} else {
		// When knowledge scope is enabled, merge the filtered sources with their configurations from the backend
		processedSources = sources.map((source) => {
			const matchingSource = allKnowledgeSourcesFiltered.find(
				(filteredSource) => filteredSource.source === source.source,
			);
			return { ...matchingSource, ...source } as KnowledgeSourceWithConfiguration;
			//Override with AgentKnowledgeSource config value
		});
	}

	const renderableSources = processedSources.filter((source) => {
		if (!source.enabled) {
			return false;
		}
		// if confluence filters has any specific pages or space filter we don't render the product
		if (
			source.source === 'confluence' &&
			(source.filters?.parent_filter?.length || source.filters?.space_filter?.length)
		) {
			return false;
		}
		// if jira filters has any specific project filter we don't render the product
		if (source.source === 'jira' && source.filters?.project_filter?.length) {
			return false;
		}
		return true;
	}) as KnowledgeSourceWithConfiguration[];

	const renderSource = (source: KnowledgeSourceWithConfiguration) => (
		<KnowledgeSourceProductUsingBEFields
			key={`${source.source}-${source.cloudId ?? ''}`}
			knowledgeSource={source}
			cloudUrl={cloudUrl}
		/>
	);

	if (!shouldShowMultiSiteExperience) {
		return {
			knowledgeSiteGroups: [],
			knowledgeProductsRender: renderableSources.length
				? renderableSources.map(renderSource)
				: null,
		};
	}

	const siteGroups: Record<string, KnowledgeSiteGroup> = {};
	renderableSources.forEach((source) => {
		if (!isSiteScopedKnowledgeSource(source.source)) {
			return;
		}

		const cloudId = source.cloudId ?? currentCloudId;
		if (!cloudId) {
			return;
		}

		siteGroups[cloudId] ??= { cloudId, filters: [], products: [] };
		siteGroups[cloudId].products.push(renderSource(source));
	});
	knowledgeFilters.forEach((filter) => {
		const cloudId = filter.cloudId ?? currentCloudId;
		if (!cloudId) {
			return;
		}

		siteGroups[cloudId] ??= { cloudId, filters: [], products: [] };
		siteGroups[cloudId].filters.push(filter);
	});

	const knowledgeSiteGroups = Object.values(siteGroups).sort((siteGroupA, siteGroupB) => {
		if (siteGroupA.cloudId === currentCloudId) {
			return -1;
		}
		if (siteGroupB.cloudId === currentCloudId) {
			return 1;
		}
		return siteGroupA.cloudId.localeCompare(siteGroupB.cloudId);
	});

	const ungroupedSources = renderableSources
		.filter((source) => !isSiteScopedKnowledgeSource(source.source))
		.map(renderSource);

	return {
		knowledgeSiteGroups,
		knowledgeProductsRender: ungroupedSources.length ? ungroupedSources : null,
	};
};

// Exported for unit tests.
export const getKnowledgeProductsRender = (
	params: KnowledgeProductsRenderParams,
): React.ReactNode[] | null => {
	const { knowledgeSiteGroups, knowledgeProductsRender } = getKnowledgeProductsRendering(params);

	if (!params.shouldShowMultiSiteExperience) {
		return knowledgeProductsRender;
	}

	return [
		<Stack key="knowledge-by-site" space="space.100">
			{knowledgeSiteGroups.map(({ cloudId, products }) => (
				<Stack key={`site-${cloudId}`} space="space.100">
					<SiteName cloudId={cloudId} />
					<Box xcss={styles.siteSources}>
						<Stack space="space.100">{products}</Stack>
					</Box>
				</Stack>
			))}
			{knowledgeProductsRender && <Stack space="space.100">{knowledgeProductsRender}</Stack>}
		</Stack>,
	];
};

export const useKnowledgeProductsRender = (
	props: AssistanceServiceInputParam,
): {
	getKnowledgeRenderForAgent: (params: KnowledgeRenderForAgentParams) => KnowledgeRenderForAgent;
} => {
	di(useEnabledKnowledgeSources, useEnsureConnectorsLoaded);

	const [storedSources] = useEnabledKnowledgeSources();
	useEnsureConnectorsLoaded({
		...props,
		...(fg('rovo_agents_unit_scoped') ? { agentCreatorType: 'CUSTOMER' as const } : {}),
		enabled: fg('rovo_agents_unit_scoped') || fg('agent-profile-icon-fix'),
	});

	const allKnowledgeSourcesFiltered = useMemo(
		() =>
			storedSources.map((knowledgeSource) => {
				const configuration =
					knowledgeSource.source === 'confluence'
						? { parent_filter: [], space_filter: [] }
						: knowledgeSource.source === 'jira'
							? { project_filter: [] }
							: undefined;
				return {
					...knowledgeSource,
					configuration,
					enabled: true,
				} as KnowledgeSourceWithConfiguration;
			}),
		[storedSources],
	);

	const getKnowledgeRenderForAgent = useCallback(
		({ agent, cloudUrl }: KnowledgeRenderForAgentParams): KnowledgeRenderForAgent => {
			const renderingParams = {
				agent,
				allKnowledgeSourcesFiltered,
				cloudUrl,
				currentCloudId: props.assistanceServiceParams.siteId,
			};

			if (!fg('rovo_agents_unit_scoped')) {
				const { knowledgeProductsRender } = getKnowledgeProductsRendering({
					...renderingParams,
					shouldShowMultiSiteExperience: false,
				});

				return {
					knowledgeProductsRender,
					knowledgeSectionProps: {
						aris: getEnabledKnowlegeArisFromAgent(agent),
						shouldGroupKnowledgeBySite: false,
					},
				};
			}

			const shouldShowMultiSiteExperience = shouldShowMultiSiteKnowledgeExperience({
				availableSources: allKnowledgeSourcesFiltered,
				currentCloudId: props.assistanceServiceParams.siteId,
				storedSources: getAgentKnowledgeSources(agent),
			});
			const knowledgeFilters = getEnabledKnowledgeFiltersFromAgent(agent);
			const { knowledgeSiteGroups, knowledgeProductsRender } = getKnowledgeProductsRendering({
				...renderingParams,
				knowledgeFilters,
				shouldShowMultiSiteExperience,
			});

			return {
				knowledgeProductsRender,
				knowledgeSectionProps: {
					knowledgeFilters,
					knowledgeSiteGroups,
					shouldGroupKnowledgeBySite: shouldShowMultiSiteExperience,
				},
			};
		},
		[allKnowledgeSourcesFiltered, props.assistanceServiceParams.siteId],
	);

	return {
		getKnowledgeRenderForAgent,
	};
};
